// One sign-in on every ACT site (ACT HQ DR-0017; Theo, 28 Sep 2026: every
// game on its own subdomain, <game>.actuniverse.com). The browser keeps
// storage per site and treats each subdomain as a separate site, so what must
// be the same everywhere (the Supabase session, the legacy guest id) lives in
// cookies scoped to the whole actuniverse.com domain, which every subdomain
// reads. Anywhere else (localhost dev, *.pages.dev previews) nothing changes:
// localStorage, as before.
//
// Import-free on purpose: the hub's feather entry (main.ts) runs this before
// the engine boots, on its cross-site redirect path.

export const SITE_DOMAIN = "actuniverse.com";

/** True on actuniverse.com and every subdomain of it. */
export function isSiteHost(hostname: string): boolean {
  return hostname === SITE_DOMAIN || hostname.endsWith(`.${SITE_DOMAIN}`);
}

/** True when this page is served from an ACT site (false in tests and dev). */
export function onSiteHost(): boolean {
  return typeof location !== "undefined" && typeof document !== "undefined" && isSiteHost(location.hostname);
}

/** The storage shape supabase-js accepts (synchronous is fine). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Whether anything is stored under `key`, readable or not (the cookie store; others use getItem). */
  hasItem?(key: string): boolean;
}

/** Anything with a `document.cookie`-style accessor (injectable for tests). */
export interface CookieJar {
  cookie: string;
}

/** Base64 characters per cookie: with the name, well under the 4 KB cookie cap. */
const CHUNK_CHARS = 3000;
/** 400 days, Chrome's cap. Safari caps script-set cookies at 7 days, the same
 *  rule it applies to localStorage, so a returning player is no worse off. */
const MAX_AGE_S = 400 * 24 * 60 * 60;

function toBase64Url(text: string): string {
  let binary = "";
  for (const byte of new TextEncoder().encode(text)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(text: string): string | null {
  try {
    const base64 = text.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64 + "===".slice((base64.length + 3) % 4));
    return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
  } catch {
    return null;
  }
}

function readJar(jar: CookieJar): Map<string, string> {
  const cookies = new Map<string, string>();
  for (const pair of jar.cookie.split(";")) {
    const at = pair.indexOf("=");
    if (at > 0) cookies.set(pair.slice(0, at).trim(), pair.slice(at + 1).trim());
  }
  return cookies;
}

/**
 * A key-value store in cookies for `domain` and all its subdomains. A value is
 * UTF-8, base64url-encoded and split over `<key>.0`, `<key>.1`, … (a session
 * is about 2.5 KB encoded, so usually one cookie). Secure + SameSite=Lax: the
 * cookie goes only over https, and never on another site's requests.
 * `lifetimeS` is how long a written value lasts (default: as long as allowed).
 */
export function cookieStore(jar: CookieJar, domain: string, lifetimeS: number = MAX_AGE_S): KeyValueStore {
  const write = (name: string, value: string, maxAgeS: number): void => {
    jar.cookie = `${name}=${value}; Domain=${domain}; Path=/; Max-Age=${maxAgeS}; SameSite=Lax; Secure`;
  };
  const clearFrom = (key: string, first: number, cookies: Map<string, string>): void => {
    for (let i = first; cookies.has(`${key}.${i}`); i += 1) write(`${key}.${i}`, "", 0);
  };
  return {
    getItem(key) {
      const cookies = readJar(jar);
      if (!cookies.has(`${key}.0`)) return null;
      let text = "";
      for (let i = 0; cookies.has(`${key}.${i}`); i += 1) text += cookies.get(`${key}.${i}`);
      return fromBase64Url(text);
    },
    setItem(key, value) {
      const cookies = readJar(jar);
      const text = toBase64Url(value);
      const count = Math.max(1, Math.ceil(text.length / CHUNK_CHARS));
      for (let i = 0; i < count; i += 1) {
        write(`${key}.${i}`, text.slice(i * CHUNK_CHARS, (i + 1) * CHUNK_CHARS), lifetimeS);
      }
      clearFrom(key, count, cookies);
    },
    removeItem(key) {
      clearFrom(key, 0, readJar(jar));
    },
    hasItem(key) {
      return readJar(jar).has(`${key}.0`);
    },
  };
}

function isJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * The session's store as supabase-js gets it: a value that isn't whole JSON reads
 * as null, never as text. A Google session is two cookie chunks, and a tab on
 * another subdomain can read between a write's chunks; supabase-js deletes a
 * stored session it can't parse, which signed the player out (masta controlla,
 * 20 Oct 2026). It reads once more first. supabase-js leaves a null alone, and
 * hasItem still says something is stored, so sign-in waits (authSession.ts). The
 * cookies themselves are written as before, so every build reads them.
 */
export function wholeJsonStore(store: KeyValueStore): KeyValueStore {
  return {
    getItem(key) {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const value = store.getItem(key);
        if (value === null || isJson(value)) return value;
      }
      return null;
    },
    setItem: (key, value) => store.setItem(key, value),
    removeItem: (key) => store.removeItem(key),
    hasItem: (key) => (store.hasItem ? store.hasItem(key) : store.getItem(key) !== null),
  };
}

/** localStorage, or null where touching it throws (some private modes). */
export function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Move a value from this site's own localStorage into the shared store, then
 * drop the local copy. Until 28 Sep 2026 the sign-in and the guest id lived in
 * play.actuniverse.com's localStorage, so the first visit there after the
 * change carries them over and nobody loses their account. `keep` decides when
 * both hold a value.
 */
export function adoptLocalValue(
  key: string,
  shared: KeyValueStore,
  local: Pick<Storage, "getItem" | "removeItem">,
  keep: (local: string, shared: string) => "local" | "shared",
): void {
  let legacy: string | null;
  try {
    legacy = local.getItem(key);
  } catch {
    return;
  }
  if (legacy === null) return;
  const current = shared.getItem(key);
  // Something is stored but couldn't be read whole (another tab mid-write): leave both, and decide on a later load.
  if (current === null && shared.hasItem?.(key)) return;
  if (current === null || keep(legacy, current) === "local") {
    shared.setItem(key, legacy);
    // Never drop the only copy: keep the local one unless the shared store took it.
    if (shared.getItem(key) !== legacy) return;
  }
  try {
    local.removeItem(key);
  } catch {
    // Leaving a stale copy is harmless: the shared value is read first.
  }
}

/** Can this page write and read back a site-wide cookie? (Cookies can be blocked.) */
export function cookiesWork(jar: CookieJar, domain: string): boolean {
  try {
    const probe = cookieStore(jar, domain);
    probe.setItem("act.probe", "1");
    const ok = probe.getItem("act.probe") === "1";
    probe.removeItem("act.probe");
    return ok;
  } catch {
    return false;
  }
}

/** The shared store on an ACT site where cookies work; otherwise null. */
export function siteCookieStore(): KeyValueStore | null {
  if (!onSiteHost() || !cookiesWork(document, SITE_DOMAIN)) return null;
  return cookieStore(document, SITE_DOMAIN);
}

type StoredSession = { userId: string; createdAtMs: number; expiresAt: number };

function readSession(raw: string): StoredSession | null {
  try {
    const parsed = JSON.parse(raw) as { expires_at?: unknown; user?: { id?: unknown; created_at?: unknown } };
    const userId = parsed.user?.id;
    if (typeof userId !== "string") return null;
    const created = Date.parse(String(parsed.user?.created_at ?? ""));
    return {
      userId,
      createdAtMs: Number.isFinite(created) ? created : Number.POSITIVE_INFINITY,
      expiresAt: typeof parsed.expires_at === "number" ? parsed.expires_at : 0,
    };
  } catch {
    return null;
  }
}

/**
 * Two stored sign-ins: keep the older account (the one with the history). A
 * player who opened a game's new subdomain before revisiting the hub got a
 * fresh guest there; their old account wins on the next hub visit. For the
 * same account, keep the fresher tokens.
 */
export function olderAccountWins(local: string, shared: string): "local" | "shared" {
  const a = readSession(local);
  const b = readSession(shared);
  if (!a) return "shared";
  if (!b) return "local";
  if (a.userId === b.userId) return a.expiresAt > b.expiresAt ? "local" : "shared";
  return a.createdAtMs < b.createdAtMs ? "local" : "shared";
}

/** supabase-js's own default storage key, so sessions stored before carry over. */
export function sessionStorageKey(supabaseUrl: string): string {
  return `sb-${new URL(supabaseUrl).hostname.split(".")[0]}-auth-token`;
}

/**
 * On an ACT site: the shared cookie store for the Supabase session, after
 * moving any session left in this site's localStorage into it. Elsewhere, or
 * where cookies are blocked: null, and supabase-js keeps its default
 * (localStorage), exactly as before.
 */
export function prepareSiteSession(supabaseUrl: string): KeyValueStore | null {
  const cookies = siteCookieStore();
  if (!cookies) return null;
  const shared = wholeJsonStore(cookies);
  const local = safeLocalStorage();
  if (local) adoptLocalValue(sessionStorageKey(supabaseUrl), shared, local, olderAccountWins);
  return shared;
}
